"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  title: "Consultation",
  description: "We assess your property's unique architectural needs and provide a comprehensive, transparent project scope and material recommendation.",
  duration: "< 24 hrs"
}, {
  number: "02",
  title: "Engineering",
  description: "Our team finalizes technical specifications, panel layouts, and material procurement to ensure precision-fit installation.",
  duration: "3-5 days"
}, {
  number: "03",
  title: "Installation",
  description: "Certified craftsmen execute the exterior renovation with meticulous attention to detail, adhering to the highest safety and quality standards.",
  duration: "Varies"
}, {
  number: "04",
  title: "Inspection",
  description: "A final walkthrough ensures every seam, panel, and finish meets our rigorous quality assurance checklist before project sign-off.",
  duration: "< 24 hrs"
}];
export default function Process() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [lineDrawn, setLineDrawn] = useState(false);
  useEffect(() => {
    if (isVisible) {
      const t = setTimeout(() => setLineDrawn(true), 100);
      return () => clearTimeout(t);
    }
  }, [isVisible]);
  return <section data-section-id="15920" id="our-proven-process" className="dark bg-background ![padding:65px_0px_75px_0px]">
      <div ref={ref} className="mx-auto max-w-[1240px] px-5 md:px-14">
        
        <div className="mx-auto max-w-3xl text-center">
          <div className="h-px w-full bg-border" />
          <p className="mt-8 text-xs font-medium tracking-[0.14em] uppercase text-muted-foreground font-default">
            The Methodology
          </p>
          <h2 className="mt-6 font-default font-semibold text-foreground" style={{
          fontSize: "clamp(2rem, 3.6vw, 3.25rem)",
          letterSpacing: "-0.018em",
          lineHeight: 1.1
        }}>
            Precision planning.{" "}
            <span className="italic relative inline-block" style={{
            fontFamily: "var(--font-serif)"
          }}>
              Flawless
              <span className="absolute left-0 right-0 block" style={{
              bottom: "-4px",
              height: "1px",
              backgroundColor: "var(--ring)"
            }} />
            </span>{" "}
            execution.
          </h2>
          <p className="mx-auto mt-6 text-muted-foreground font-default" style={{
          fontSize: "clamp(1.0625rem, 1.2vw, 1.1875rem)",
          lineHeight: 1.6,
          maxWidth: "52ch"
        }}>
            From initial site assessment to final inspection, our process is engineered for homeowners who demand architectural integrity and lasting performance.
          </p>
        </div>

        
        <div className="hidden md:block mt-20">
          <div className="relative grid grid-cols-4 gap-8">
            
            <div className="absolute left-0 right-0 pointer-events-none" style={{
            top: "50%",
            height: "1px",
            transform: "translateY(-0.5px)"
          }}>
              <div className="h-full origin-left" style={{
              backgroundColor: "var(--ring)",
              transform: lineDrawn ? "scaleX(1)" : "scaleX(0)",
              transition: "transform 900ms cubic-bezier(0.2, 0.7, 0.2, 1)"
            }} />
            </div>

            {steps.map((step, i) => <div data-index={i} key={step.number} className="relative flex flex-col items-center text-center group cursor-default h-full" onMouseEnter={() => setHoveredIndex(i)} onMouseLeave={() => setHoveredIndex(null)}>
                
                <div className="pb-12">
                  <p className="font-mono text-muted-foreground" style={{
                fontSize: "0.8125rem",
                letterSpacing: "0.02em"
              }}>
                    {step.number}
                  </p>
                  <h3 className="mt-3 font-default font-semibold text-foreground relative inline-block" style={{
                fontSize: "clamp(1.25rem, 1.6vw, 1.5rem)",
                letterSpacing: "-0.01em",
                lineHeight: 1.25
              }}>
                    {step.title}
                    <span className="absolute left-0 block" style={{
                  bottom: "-4px",
                  height: "1px",
                  backgroundColor: "var(--ring)",
                  width: hoveredIndex === i ? "100%" : "0%",
                  transition: "width 220ms cubic-bezier(0.2, 0.7, 0.2, 1)"
                }} />
                  </h3>
                </div>

                
                <div className="relative z-10 rounded-lg shrink-0" style={{
              backgroundColor: "var(--ring)",
              width: hoveredIndex === i ? "12px" : "8px",
              height: hoveredIndex === i ? "12px" : "8px",
              transform: lineDrawn ? "scale(1)" : "scale(0.6)",
              opacity: lineDrawn ? 1 : 0,
              transition: `transform 220ms cubic-bezier(0.2, 0.7, 0.2, 1) ${200 + i * 180}ms, opacity 220ms ease-out ${200 + i * 180}ms, width 220ms ease, height 220ms ease`
            }} />

                
                <div className="pt-12 flex flex-col flex-grow w-full justify-between">
                  <p className="text-muted-foreground font-default flex-grow" style={{
                fontSize: "clamp(0.9375rem, 1vw, 1rem)",
                lineHeight: 1.62
              }}>
                    {step.description}
                  </p>
                  <p className="font-mono text-foreground pt-5" style={{
                fontSize: "0.8125rem",
                letterSpacing: "0.02em"
              }}>
                    {step.duration}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        
        <div className="md:hidden mt-16 relative">
          
          <div className="absolute top-0 bottom-0 pointer-events-none" style={{
          left: "7px",
          width: "1px"
        }}>
            <div className="w-full origin-top" style={{
            backgroundColor: "var(--ring)",
            height: "100%",
            transform: lineDrawn ? "scaleY(1)" : "scaleY(0)",
            transition: "transform 900ms cubic-bezier(0.2, 0.7, 0.2, 1)"
          }} />
          </div>

          <div className="space-y-12">
            {steps.map((step, i) => <div data-index={i} key={step.number} className="relative pl-10">
                
                <div className="absolute rounded-lg" style={{
              left: "3px",
              top: "8px",
              width: "8px",
              height: "8px",
              backgroundColor: "var(--ring)",
              transform: lineDrawn ? "scale(1)" : "scale(0.6)",
              opacity: lineDrawn ? 1 : 0,
              transition: `transform 220ms cubic-bezier(0.2, 0.7, 0.2, 1) ${200 + i * 180}ms, opacity 220ms ease-out ${200 + i * 180}ms`
            }} />

                <p className="font-mono text-muted-foreground" style={{
              fontSize: "0.8125rem",
              letterSpacing: "0.02em"
            }}>
                  {step.number}
                </p>
                <h3 className="mt-2 font-default font-semibold text-foreground" style={{
              fontSize: "1.25rem",
              letterSpacing: "-0.01em",
              lineHeight: 1.25
            }}>
                  {step.title}
                </h3>
                <p className="mt-3 text-muted-foreground font-default" style={{
              fontSize: "0.9375rem",
              lineHeight: 1.62
            }}>
                  {step.description}
                </p>
                <p className="mt-4 font-mono text-foreground" style={{
              fontSize: "0.8125rem",
              letterSpacing: "0.02em"
            }}>
                  {step.duration}
                </p>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}